import { ClientLoggingService } from '../../../../shared/client-logging.service';
import { Component, OnInit, ViewChild, AfterViewInit, OnDestroy, ElementRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTableModule } from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { PlanReviewerService, ComplianceErrorCategory, CodeBook } from '../../../../shared/plan-reviewer.service';
import { ArchitecturalPlanPage } from '../../../../shared/models/architectural-plan-page.model';
import { PlanRefreshService } from '../../../../shared/plan-refresh.service';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatListModule } from '@angular/material/list';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { HandsetDialogService } from '../../../../shared/handset-dialog.service';
import { Router } from '@angular/router';
import { getTaskUrl } from '../../../../shared/task-navigation.util';
import { RationaleDialogComponent, RationaleDialogData } from './rationale-dialog/rationale-dialog.component';
import { MatDialogModule } from '@angular/material/dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TabService } from '../../../../shared/tab.service';
import {
  SelectedSectionsToolbarComponent,
  SelectedSectionsToolbarAction,
} from './selected-sections-toolbar/selected-sections-toolbar.component';
import { Tab } from '../../../../shared/models/tab.model';
import { ComplianceReportComponent } from './compliance-report-tab/compliance-report-tab.component';
import { ComplianceFallbackComponent } from './compliance-fallback/compliance-fallback.component';
import { MatChipsModule } from '@angular/material/chips';
import { CodeApplicabilityDialogComponent } from './code-applicability-dialog/code-applicability-dialog.component';
import { ComplianceRiskLabelPipe } from '../../../../shared/pipes/compliance-risk-label.pipe';
import {
  ColumnFilterComponent,
  ColumnFilterConfig,
  ColumnFilterCriterion,
  CategoricalFilterCriterion,
  matchesCategoricalCriterion,
} from '../../../../shared/table-filter';

import { trigger, style, transition, animate } from '@angular/animations';
import { ComplianceStateService, ComplianceComponentState } from '../../../../shared/compliance-state.service';
import { TaskCounterService } from '../../../../shared/task-counter.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { getChapterUrlSlug } from '../../../../shared/utils/icc-url-utils';
import { BillingService } from '../../../../shared/billing.service';
import { showBillingAwareErrorSnackbar } from '../../../../shared/billing-error-utils';
import { RawEvidenceCitation } from '../../../../shared/citations/evidence-citation.mapper';
import { buildCodeSectionTitleIndex, mergeCodeSectionTitles } from '../../../../shared/citations/code-section-title';

export interface ComplianceSection {
  title: string;
  sectionId: string;
  contentId?: number;
  relevance: 'High' | 'Medium' | 'Low' | 'Not Relevant' | '-';
  status: 'Compliant' | 'Non-Compliant' | 'Partially Compliant' | 'Not Applicable' | 'Unknown' | '-';
  rationale?: string;
  depth?: number;
  hierarchy?: { title: string; id?: string; chapterSlug?: string }[];
  complianceStatement?: string;
  complianceStatementTldr?: string;
  generatedByModel?: string;
  createdTime?: Date;
  codeBook?: any;
  selected?: boolean; // NEW: Selection state for compliance report generation

  /**
   * Raw structured evidence backing this section's verdict (Issue #1773).
   *
   * Carried through from `PageSectionComplianceReport.evidence_chain` in the wire shape rather than
   * the validated one, because validation needs the report's own project identity and belongs in
   * `mapEvidenceChain`, at the point of rendering, not in the table that lists sections. Empty for
   * every report cached before structured evidence existed, and for the legacy single-turn path.
   */
  evidenceChain?: RawEvidenceCitation[];

  /**
   * Project the report was generated for. This is the authorization anchor a citation is checked
   * against, so it must come from the report itself and not from whatever the reader is looking at.
   */
  architecturalPlanId?: string;

  /**
   * File and page the report was generated for.
   *
   * Named distinctly from the active page on purpose. Cross-page evidence has to resolve against
   * the page it was found on, and conflating the two is exactly the defect the issue calls out:
   * "links to the actual evidence page and file, rather than the page whose report tab is currently
   * open".
   */
  evidenceFileId?: string;
  evidencePageNumber?: number;
}

/** Main compliance analysis view: renders a hierarchical table of building-code sections with relevance/compliance status, filtering, and bulk-selection for report generation. */
@Component({
  selector: 'app-compliance',
  imports: [
    CommonModule,
    MatProgressBarModule,
    MatTableModule,
    MatCardModule,
    MatSelectModule,
    MatFormFieldModule,
    FormsModule,
    ReactiveFormsModule,
    MatMenuModule,
    MatIconModule,
    MatButtonModule,
    MatListModule,
    MatDialogModule,
    MatCheckboxModule,
    MatTooltipModule,
    ComplianceFallbackComponent,
    SelectedSectionsToolbarComponent,
    MatChipsModule,
    ComplianceRiskLabelPipe,
    ColumnFilterComponent,
  ],
  templateUrl: './compliance.component.html',
  styleUrls: ['./compliance.component.scss'],
  animations: [
    trigger('rowAnimation', [
      transition(':enter', [
        style({ height: '0px', opacity: 0, transform: 'translateY(-10px)', overflow: 'hidden' }),
        animate('150ms ease-out', style({ height: '*', opacity: 1, transform: 'translateY(0)' })),
      ]),
      transition(':leave', [
        animate(
          '150ms ease-in',
          style({ height: '0px', opacity: 0, transform: 'translateY(-10px)', overflow: 'hidden' })
        ),
      ]),
    ]),
  ],
})
export class ComplianceComponent implements OnInit, AfterViewInit, OnDestroy {
  private planReviewerService = inject(PlanReviewerService);
  private dialog = inject(MatDialog);
  private handsetDialog = inject(HandsetDialogService);
  router = inject(Router);
  private tabService = inject(TabService);
  private complianceStateService = inject(ComplianceStateService);
  private taskCounterService = inject(TaskCounterService);
  private snackBar = inject(MatSnackBar);
  private planRefreshService = inject(PlanRefreshService);
  private billingService = inject(BillingService);

  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger;
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;

  loading = false;
  complianceData: { sections: ComplianceSection[] } | null = null;
  displayedColumns: string[] = ['section', 'relevance', 'compliance'];
  activePlanPage: ArchitecturalPlanPage | null = null;

  // Phase 3: Error handling and fallback UI
  complianceError: ComplianceErrorCategory | null = null;
  showFallbackUI = false;

  // Relevance filter properties
  selectedRelevanceValues: string[] = [];
  relevanceOptions = ['High', 'Medium', 'Low', 'Not Relevant'];
  relevanceFilterControl = new FormControl<string[]>([]);
  relevanceFilterConfig: ColumnFilterConfig = {
    columnId: 'relevance',
    columnLabel: 'Relevance',
    type: 'categorical',
    options: [
      { value: 'High', label: 'High', chipClass: 'relevance-high' },
      { value: 'Medium', label: 'Medium', chipClass: 'relevance-medium' },
      { value: 'Low', label: 'Low', chipClass: 'relevance-low' },
      { value: 'Not Relevant', label: 'Not Relevant', chipClass: 'relevance-not-relevant' },
    ],
  };

  // Compliance filter properties
  selectedComplianceValues: string[] = [];
  complianceOptions = ['Compliant', 'Non-Compliant', 'Partially Compliant', 'Not Applicable', 'Unknown'];
  complianceFilterControl = new FormControl<string[]>([]);
  complianceFilterConfig: ColumnFilterConfig = {
    columnId: 'compliance',
    columnLabel: 'Compliance',
    type: 'categorical',
    options: [
      { value: 'Compliant', label: 'Compliant', chipClass: 'compliance-compliant' },
      { value: 'Non-Compliant', label: 'Non-Compliant', chipClass: 'compliance-non-compliant' },
      { value: 'Partially Compliant', label: 'Partially Compliant', chipClass: 'compliance-partially-compliant' },
      { value: 'Not Applicable', label: 'Not Applicable', chipClass: 'compliance-not-applicable' },
      { value: 'Unknown', label: 'Unknown', chipClass: 'compliance-unknown' },
    ],
  };

  private _relevanceCriterion?: CategoricalFilterCriterion;
  private _lastRelevanceValues?: string[];

  get relevanceCriterion(): CategoricalFilterCriterion | undefined {
    if (this.selectedRelevanceValues.length === 0) {
      this._relevanceCriterion = undefined;
      this._lastRelevanceValues = undefined;
    } else if (
      !this._relevanceCriterion ||
      this._lastRelevanceValues?.length !== this.selectedRelevanceValues.length ||
      !this.selectedRelevanceValues.every((v, i) => v === this._lastRelevanceValues?.[i])
    ) {
      this._lastRelevanceValues = [...this.selectedRelevanceValues];
      this._relevanceCriterion = { type: 'categorical', values: this._lastRelevanceValues };
    }
    return this._relevanceCriterion;
  }

  private _complianceCriterion?: CategoricalFilterCriterion;
  private _lastComplianceValues?: string[];

  get complianceCriterion(): CategoricalFilterCriterion | undefined {
    if (this.selectedComplianceValues.length === 0) {
      this._complianceCriterion = undefined;
      this._lastComplianceValues = undefined;
    } else if (
      !this._complianceCriterion ||
      this._lastComplianceValues?.length !== this.selectedComplianceValues.length ||
      !this.selectedComplianceValues.every((v, i) => v === this._lastComplianceValues?.[i])
    ) {
      this._lastComplianceValues = [...this.selectedComplianceValues];
      this._complianceCriterion = { type: 'categorical', values: this._lastComplianceValues };
    }
    return this._complianceCriterion;
  }

  onRelevanceCriterionChange(criterion?: ColumnFilterCriterion): void {
    const values = criterion && criterion.type === 'categorical' ? criterion.values : [];
    this.selectedRelevanceValues = values;
    this.relevanceFilterControl.setValue(values, { emitEvent: false });
    this.onRelevanceFilterChange();
  }

  onComplianceCriterionChange(criterion?: ColumnFilterCriterion): void {
    const values = criterion && criterion.type === 'categorical' ? criterion.values : [];
    this.selectedComplianceValues = values;
    this.complianceFilterControl.setValue(values, { emitEvent: false });
    this.onComplianceFilterChange();
  }

  // NEW: Section selection for compliance report generation
  selectedSections: ComplianceSection[] = [];

  // Constants for padding calculation
  private readonly BASE_PADDING = 8;
  private readonly INDENT_SIZE = 24;

  // Track expanded/collapsed state by sectionId
  expandedSectionIds = new Set<string>();
  childrenMap = new Map<string, ComplianceSection[]>();

  // Track scroll state for header shadow
  isScrolled = false;

  private destroy$ = new Subject<void>();

  get visibleSections(): ComplianceSection[] {
    if (!this.complianceData) return [];

    const visible = [];
    for (const section of this.complianceData.sections) {
      // Root items are always potentially visible
      if (section.depth === 0) {
        visible.push(section);
        if (this.isExpanded(section)) {
          // Add all expanded descendants
          this.addExpandedDescendants(section, visible);
        }
      }
    }
    return visible.filter(section => this.sectionMatchesFilter(section));
  }

  private addExpandedDescendants(parent: ComplianceSection, visibleList: ComplianceSection[]) {
    const children = this.childrenMap.get(parent.sectionId);
    if (!children) return;

    for (const child of children) {
      visibleList.push(child);
      if (this.isExpanded(child)) {
        this.addExpandedDescendants(child, visibleList);
      }
    }
  }

  ngOnDestroy(): void {
    // Save current state before component is destroyed
    this.saveCurrentState();
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit() {
    this.tabService.activePlanPage$.pipe(takeUntil(this.destroy$)).subscribe(page => {
      if (page && page.getPageNumber() !== this.activePlanPage?.getPageNumber()) {
        this.activePlanPage = page;
        this.fetchCompliance(page);

        // Load existing tasks for this project to ensure task detection works. Best-effort:
        // this component doesn't drive a task list UI, so a failure here just means task
        // detection may be incomplete until the next successful load.
        this.taskCounterService.loadExistingTasksForProject(page.getArchitecturalPlanId()).catch(error => {
          ClientLoggingService.log(
            'ERROR',
            '[Compliance] Error loading existing tasks for project:',
            page.getArchitecturalPlanId(),
            error
          );
        });
      }
    });

    // Listen for compliance data refresh events (e.g., after code applicability analysis completion)
    this.planRefreshService.complianceRefresh$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ planId, pageNumber, reason }) => {
        ClientLoggingService.log(
          'INFO',
          `[ComplianceComponent] Compliance refresh requested for ${planId} page ${pageNumber}: ${reason}`
        );

        // Only refresh if it matches the current page
        if (
          this.activePlanPage &&
          this.activePlanPage.getArchitecturalPlanId() === planId &&
          this.activePlanPage.getPageNumber() === pageNumber
        ) {
          ClientLoggingService.log('INFO', `[ComplianceComponent] Refreshing compliance data for current page`);

          // Clear caches and refetch compliance data
          this.planReviewerService.clearPlanDataCache();
          this.fetchCompliance(this.activePlanPage);
        }
      });

    // Add debug method to window for troubleshooting
    (window as any).debugCompliance = () => {
      ClientLoggingService.log('INFO', '=== COMPLIANCE DEBUG INFO ===');
      ClientLoggingService.log('INFO', 'Visible sections:', this.visibleSections);
      ClientLoggingService.log('INFO', 'Children map:', this.childrenMap);
      ClientLoggingService.log('INFO', 'Expanded section IDs:', this.expandedSectionIds);

      // Find FLBC sections specifically
      const flbcSections = this.visibleSections.filter((s: any) => s.codeBook?.abbreviation === 'FLBC');
      ClientLoggingService.log('INFO', 'FLBC sections:', flbcSections);

      // Find FLBC Chapter 1
      const flbcChapter1 = flbcSections.find((s: any) => s.title.includes('Chapter 1'));
      if (flbcChapter1) {
        ClientLoggingService.log('INFO', 'FLBC Chapter 1:', flbcChapter1);
        ClientLoggingService.log('INFO', 'FLBC Chapter 1 children:', this.childrenMap.get(flbcChapter1.sectionId));
        ClientLoggingService.log('INFO', 'FLBC Chapter 1 is expanded:', this.isExpanded(flbcChapter1));
        ClientLoggingService.log('INFO', 'FLBC Chapter 1 has children:', this.hasChildren(flbcChapter1));
      }
    };

    // Subscribe to relevance filter changes
    this.relevanceFilterControl.valueChanges.subscribe(values => {
      this.selectedRelevanceValues = values || [];
      this.onRelevanceFilterChange();
    });

    // Subscribe to compliance filter changes
    this.complianceFilterControl.valueChanges.subscribe(values => {
      this.selectedComplianceValues = values || [];
      this.onComplianceFilterChange();
    });
  }

  ngAfterViewInit() {
    // Configure menu to not close on checkbox clicks
    if (this.menuTrigger) {
      this.menuTrigger.menuClosed.subscribe(() => {
        if (this.menuTrigger.menu) {
          this.menuTrigger.menu.focusFirstItem = () => {};
        }
      });
    }
  }

  onMenuClick(event: MouseEvent) {
    // Prevent menu from closing when clicking inside the menu content
    event.stopPropagation();
  }

  // Check if a section has children
  hasChildren(section: ComplianceSection): boolean {
    return this.childrenMap.has(section.sectionId) && (this.childrenMap.get(section.sectionId)?.length ?? 0) > 0;
  }

  // Toggle a row's expanded/collapsed state
  toggleRow(section: ComplianceSection, event?: Event) {
    // Check for shift key (works with both mouse and keyboard events)
    const isShiftClick = event && 'shiftKey' in event ? (event as KeyboardEvent | MouseEvent).shiftKey : false;

    if (isShiftClick) {
      this.toggleRowDeep(section);
    } else {
      this.toggleRowSimple(section);
    }
    // Save state when expansion changes
    this.saveCurrentState();
  }

  private toggleRowSimple(section: ComplianceSection) {
    if (this.expandedSectionIds.has(section.sectionId)) {
      this.expandedSectionIds.delete(section.sectionId);
    } else {
      this.expandedSectionIds.add(section.sectionId);
    }
  }

  private toggleRowDeep(section: ComplianceSection) {
    const isCurrentlyExpanded = this.expandedSectionIds.has(section.sectionId);
    const descendantIds = this.getAllDescendantIds(section);

    if (isCurrentlyExpanded) {
      // If it's expanded, collapse it and all its children.
      this.expandedSectionIds.delete(section.sectionId);
      for (const id of descendantIds) {
        this.expandedSectionIds.delete(id);
      }
    } else {
      // If it's collapsed, expand it and all its children.
      this.expandedSectionIds.add(section.sectionId);
      for (const id of descendantIds) {
        this.expandedSectionIds.add(id);
      }
    }
  }

  private getAllDescendantIds(parent: ComplianceSection): string[] {
    const descendants: string[] = [];
    const children = this.childrenMap.get(parent.sectionId);
    if (!children) return [];

    for (const child of children) {
      descendants.push(child.sectionId);
      descendants.push(...this.getAllDescendantIds(child));
    }
    return descendants;
  }

  // Check if a row is expanded
  isExpanded(section: ComplianceSection): boolean {
    return this.expandedSectionIds.has(section.sectionId);
  }

  // Expand/collapse all rows
  setAllExpanded(expanded: boolean) {
    if (!this.complianceData) return;
    this.expandedSectionIds.clear();
    if (expanded) {
      for (const section of this.complianceData.sections) {
        if (this.hasChildren(section)) {
          this.expandedSectionIds.add(section.sectionId);
        }
      }
    }
    // Save state when using expand/collapse all
    this.saveCurrentState();
  }

  // Check if a section or any of its children match the relevance filter
  private sectionMatchesFilter(section: ComplianceSection): boolean {
    if (!this.selectedRelevanceValues.length && !this.selectedComplianceValues.length) return true;

    // Check if the section itself matches relevance filter
    const matchesRelevance = matchesCategoricalCriterion(section.relevance, this.relevanceCriterion);

    // Check if the section itself matches compliance filter
    const matchesCompliance = matchesCategoricalCriterion(section.status, this.complianceCriterion);

    // Section must match both filters (AND logic)
    const sectionMatches = matchesRelevance && matchesCompliance;

    if (sectionMatches) return true;

    // Check if any children match
    const children = this.childrenMap.get(section.sectionId) || [];
    return children.some(child => this.sectionMatchesFilter(child));
  }

  // Handle relevance filter changes
  onRelevanceFilterChange() {
    // Ensure parent sections are expanded if they have matching children
    if (this.complianceData) {
      for (const section of this.complianceData.sections) {
        if (this.hasChildren(section) && this.sectionMatchesFilter(section)) {
          this.expandedSectionIds.add(section.sectionId);
        }
      }
    }
    // Save state when filters change
    this.saveCurrentState();
  }

  // Handle compliance filter changes
  onComplianceFilterChange() {
    // Ensure parent sections are expanded if they have matching children
    if (this.complianceData) {
      for (const section of this.complianceData.sections) {
        if (this.hasChildren(section) && this.sectionMatchesFilter(section)) {
          this.expandedSectionIds.add(section.sectionId);
        }
      }
    }
    // Save state when filters change
    this.saveCurrentState();
  }

  private buildAndFlattenSectionTree(sections: ComplianceSection[]): ComplianceSection[] {
    this.childrenMap.clear();
    const allSectionsById = new Map<string, ComplianceSection>();

    // First pass: index all sections by their ID and set depth
    for (const section of sections) {
      allSectionsById.set(section.sectionId, section);
      section.depth = (section.hierarchy || []).length - 1;
    }

    // Second pass: build the childrenMap based on hierarchy
    for (const section of sections) {
      if (section.hierarchy && section.hierarchy.length > 1) {
        const parentHierarchyItem = section.hierarchy[section.hierarchy.length - 2];
        const parentId = parentHierarchyItem.id;
        if (parentId && allSectionsById.has(parentId)) {
          if (!this.childrenMap.has(parentId)) {
            this.childrenMap.set(parentId, []);
          }
          this.childrenMap.get(parentId)!.push(section);
        }
      }
    }

    // The tooltip on an inline code link names the provision it points at. Two sources, merged:
    // this page's compliance tree, which is available now but filtered to applicable rows, and the
    // book's full table of contents, which reaches every depth and is where subsections like
    // 905.3.1 and 905.5.1 actually live. The tree alone left the deepest references — the ones most
    // worth naming — with no title, and a deep-linked report with none at all.
    //
    // Enriching the tooltips must never be able to break the tree this method exists to build, so
    // ALL of it is guarded — the first publish included, not only the asynchronous part. The tree
    // build itself is above this and deliberately outside the guard: the point is to protect the
    // tree from the tooltip, and wrapping the tree too would invert that. A failure costs richer
    // wording and nothing else, every tooltip falling back to its number, and it is LOGGED rather
    // than swallowed — a guard that degrades silently destroys exactly the signal it should keep.
    try {
      const treeTitles = buildCodeSectionTitleIndex(sections);
      this.tabService.setCodeSectionTitles(treeTitles);

      const books = new Map<string, CodeBook>();
      for (const section of sections) {
        const book = (section as { codeBook?: CodeBook }).codeBook;
        if (book?.id && !books.has(book.id)) books.set(book.id, book);
      }
      // Cached per book by the service, so this is a map read once the page has drawn its tree.
      Promise.all([...books.values()].map(book => this.planReviewerService.getCodeSectionTitleIndex(book)))
        .then(indexes => {
          const full = new Map<string, string>();
          for (const index of indexes) {
            for (const [number, title] of index) if (!full.has(number)) full.set(number, title);
          }
          this.tabService.setCodeSectionTitles(mergeCodeSectionTitles(full, treeTitles));
        })
        .catch(error => {
          ClientLoggingService.log('WARN', '[ComplianceComponent] code section titles unavailable:', error);
        });
    } catch (error) {
      ClientLoggingService.log('WARN', '[ComplianceComponent] code section titles unavailable:', error);
    }

    // The service already provides a flattened list in a suitable order
    return sections;
  }

  fetchCompliance(page: ArchitecturalPlanPage) {
    ClientLoggingService.log('INFO', '[ComplianceComponent] fetchCompliance called for page:', page.getPageNumber());
    this.loading = true;
    this.complianceData = null; // Clear old data to show loader
    this.complianceError = null; // Clear any previous errors
    this.showFallbackUI = false; // Reset fallback UI state
    this.selectedSections = []; // Selections belong to the previously loaded page/report
    this.relevanceCountsCache.clear(); // Clear the cache for relevance counts

    // Allow UI to update and show loader before heavy processing
    setTimeout(() => {
      this.planReviewerService
        .getPageComplianceReport(page)
        .then(data => {
          ClientLoggingService.log(
            'INFO',
            '[ComplianceComponent] Compliance data received for page:',
            page.getPageNumber()
          );
          ClientLoggingService.log('INFO', '[ComplianceComponent] Raw data structure:', data);
          ClientLoggingService.log('INFO', '[ComplianceComponent] Data has sections:', !!data?.sections);
          ClientLoggingService.log('INFO', '[ComplianceComponent] Sections length:', data?.sections?.length || 0);
          ClientLoggingService.log('INFO', '[ComplianceComponent] First few sections:', data?.sections?.slice(0, 3));

          // Phase 3: Check if the data contains error information from enhanced service
          if (data && data.errorCategories && data.errorCategories.length > 0) {
            this.handleComplianceErrors(data.errorCategories);
            return;
          }

          if (data && Array.isArray(data.sections)) {
            // This can be slow, so it's wrapped in the timeout
            const processedSections = this.buildAndFlattenSectionTree(data.sections);
            ClientLoggingService.log(
              'INFO',
              '[ComplianceComponent] Processed sections length:',
              processedSections.length
            );
            ClientLoggingService.log('INFO', '[ComplianceComponent] First processed section:', processedSections[0]);

            if (processedSections.length === 0) {
              ClientLoggingService.log('INFO', '[ComplianceComponent] No sections in data, showing empty state');
              this.complianceData = null;
              this.loading = false;
              this.showEmptyStateMessage();
              return;
            }

            this.complianceData = { ...data, sections: processedSections };
            this.loading = false;

            // Restore state after data is loaded
            this.restoreState();
          } else {
            // No data but no explicit error - might be empty result
            ClientLoggingService.log('INFO', '[ComplianceComponent] No sections in data, showing empty state');
            this.complianceData = null;
            this.loading = false;
            this.showEmptyStateMessage();
          }
        })
        .catch(err => {
          ClientLoggingService.log('ERROR', '[ComplianceComponent] Error fetching compliance data:', err);
          this.handleComplianceError(err);
        });
    }, 0); // Use a timeout of 0 to defer to the next event cycle
  }

  /**
   * Phase 3: Handle compliance errors with categorization and appropriate UI response
   */
  private handleComplianceError(error: any): void {
    // Use the service's error categorization (we'll need to expose this method)
    this.complianceError = this.categorizeError(error);
    this.complianceData = null;
    this.loading = false;
    this.showFallbackUI = this.complianceError.showFallbackUI;

    // Note: Removed redundant snack bar notifications since the fallback UI
    // (ComplianceFallbackComponent) already provides comprehensive user feedback
    // with better UX including detailed messaging, action buttons, and technical details
  }

  /**
   * Phase 3: Handle multiple compliance errors from enhanced service response
   */
  private handleComplianceErrors(errorCategories: ComplianceErrorCategory[]): void {
    // Use the most severe error for UI display
    const severityOrder = { error: 3, warning: 2, info: 1 };
    const primaryError = errorCategories.reduce((prev, current) =>
      severityOrder[current.severity] > severityOrder[prev.severity] ? current : prev
    );

    this.complianceError = primaryError;
    this.complianceData = null;
    this.loading = false;
    this.showFallbackUI = primaryError.showFallbackUI;

    // Note: Removed redundant snack bar notification since the fallback UI
    // (ComplianceFallbackComponent) already provides comprehensive error display
  }

  /**
   * Simple error categorization for cases where service categorization isn't available
   */
  private categorizeError(error: any): ComplianceErrorCategory {
    // Basic error categorization as fallback
    if (error.message && (error.message.includes('page-only mode') || error.message.includes('not available'))) {
      return {
        type: 'ANALYSIS_NOT_AVAILABLE',
        severity: 'info',
        userMessage:
          'Code compliance analysis is not available for this project. You may need to initiate compliance analysis first.',
        technicalMessage: error.message,
        canRetry: false,
        showFallbackUI: true,
      };
    }

    return {
      type: 'GRPC_ERROR',
      severity: 'error',
      userMessage: 'An error occurred while loading compliance data.',
      technicalMessage: error.message || 'Unknown error',
      canRetry: true,
      showFallbackUI: true,
    };
  }

  /**
   * Show message when no compliance data is available but no error occurred
   */
  private showEmptyStateMessage(): void {
    // Note: Removed redundant snack bar notification since the HTML template
    // already presents the no-data state and its next action inline.
  }

  /**
   * Phase 3: Retry compliance data fetch (called from fallback UI)
   */
  onRetryCompliance(): void {
    if (this.activePlanPage) {
      this.fetchCompliance(this.activePlanPage);
    }
  }

  /**
   * Phase 3: Handle contact support request (called from fallback UI)
   */
  onContactSupport(): void {
    // Open support contact method (could be email, chat, etc.)
    const supportEmail = 'support@permitproof.app';
    const subject = encodeURIComponent('Code Compliance Issue');
    const body = encodeURIComponent(
      `I'm experiencing an issue with code compliance features.\n\n` +
        `Project: ${this.activePlanPage?.getArchitecturalPlanId()}\n` +
        `Page: ${this.activePlanPage?.getPageNumber()}\n` +
        `Error: ${this.complianceError?.technicalMessage}\n\n` +
        `Please help resolve this issue.`
    );

    window.open(`mailto:${supportEmail}?subject=${subject}&body=${body}`);
  }

  /**
   * Handle insights workflow start from fallback UI
   */
  onStartInsights(insightType: string): void {
    ClientLoggingService.log('INFO', '[ComplianceComponent] Starting insights workflow:', insightType);
    // Refresh the compliance data to pick up any new tasks
    if (this.activePlanPage) {
      this.fetchCompliance(this.activePlanPage);
    }
  }

  /**
   * Check if there's an active code applicability task for the current page
   */
  hasActiveCodeApplicabilityTask(): boolean {
    if (!this.activePlanPage) return false;

    const activeTasks = this.taskCounterService.getTasksForProject(this.activePlanPage.getArchitecturalPlanId());

    return activeTasks.some(
      task =>
        task.metadata?.['taskType'] === 'code-applicability' &&
        task.metadata?.['pageNumber'] === this.activePlanPage!.getPageNumber() &&
        (task.status === 'pending' || task.status === 'processing')
    );
  }

  /**
   * Get the active code applicability task for the current page (if any)
   */
  navigateToActiveTask(taskId?: string): void {
    if (this.activePlanPage) {
      const planId = this.activePlanPage.getArchitecturalPlanId();
      this.router.navigate(getTaskUrl(planId, taskId));
    }
  }

  getActiveCodeApplicabilityTask(): any {
    if (!this.activePlanPage) return null;

    const activeTasks = this.taskCounterService.getTasksForProject(this.activePlanPage.getArchitecturalPlanId());
    return (
      activeTasks.find(
        task =>
          task.metadata?.['taskType'] === 'code-applicability' &&
          task.metadata?.['pageNumber'] === this.activePlanPage!.getPageNumber() &&
          (task.status === 'pending' || task.status === 'processing')
      ) || null
    );
  }

  /**
   * Open dialog to add additional compliance analysis (e.g. for another book/chapter)
   */
  openAddComplianceDialog(): void {
    if (!this.activePlanPage) return;

    const dialogRef = this.handsetDialog.open(CodeApplicabilityDialogComponent, {
      width: '800px',
      maxHeight: '90vh',
      disableClose: true,
      data: {
        activePlanPage: this.activePlanPage,
        existingSections: this.complianceData?.sections || [],
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Task started, we can refresh or let the socket/polling handle it
        // The dialog already shows a snackbar navigating to tasks
      }
    });
  }

  getComplianceClass(status: string | undefined): string {
    if (!status) return 'compliance--';
    return 'compliance-' + status.toLowerCase().replace(/\s+/g, '-');
  }

  getSectionPadding(depth: number | undefined): string {
    const d = depth || 0;
    // Uses container query units (cqi) to squeeze the 24px pad multiplier to 0px as container drops to 300px
    return `calc(8px + ${d} * clamp(0px, (100cqi - 300px) * 0.08, 24px))`;
  }

  getSectionTitleParts(title: string): { number: string; name: string } {
    if (!title) return { number: '', name: '' };

    // Check if it's a chapter: "Chapter 1 Scope" or "Chapter 1: Scope"
    const chapterMatch = title.match(/^(Chapter\s+[0-9A-Z]+)[:\s]+(.*)$/i);
    if (chapterMatch) {
      return { number: chapterMatch[1], name: chapterMatch[2] };
    }

    // Check if it's explicitly "Section X Y": "Section 301 SCOPE", "Section [A] 101.1", etc.
    const sectionWordMatch = title.match(/^(Section\s+(?:\[[A-Z]+\]\s*)?[A-Z0-9.]+)(?:[\s:,-]+)(.*)$/i);
    if (sectionWordMatch) {
      return { number: sectionWordMatch[1], name: sectionWordMatch[2] };
    }

    // Check for standard section pattern: optional bracket prefix like "[A] ", followed by numbers/dots/letters like "101.1.1" or "A.1" or "101.A"
    // e.g. "[A] 101.1 Title", "101.1 Title", "P2904.1.1 Required sprinkler locations"
    const sectionMatch = title.match(/^((?:\[[A-Z]+\]\s*)?[A-Z0-9](?:[A-Z0-9.]*[A-Z0-9])?)[\s:,-]+(.*)$/i);
    if (sectionMatch) {
      return { number: sectionMatch[1], name: sectionMatch[2] };
    }

    // Fallback if we can't cleanly parse it
    return { number: title, name: '' };
  }

  getChapterSlug(hierarchy?: { title: string; id?: string; chapterSlug?: string }[]): string {
    // After adding Book level, chapter is now at index 1
    if (hierarchy && hierarchy.length > 1 && hierarchy[1].chapterSlug) {
      return hierarchy[1].chapterSlug;
    }
    return '';
  }

  /**
   * Generate the appropriate ICC URL for a section based on its hierarchy level
   */
  getIccUrlForSection(section: ComplianceSection): string {
    const codeBook = section.codeBook;
    if (!codeBook) return '#';

    const documentSlug = codeBook.document_slug;
    const hierarchy = section.hierarchy || [];

    // Book level (e.g., IBC 2021)
    if (hierarchy.length === 1) {
      return `https://codes.iccsafe.org/content/${documentSlug}`;
    }

    // Chapter, SubChapter, or Section level
    if (hierarchy.length > 1) {
      const chapterItem = hierarchy[1];
      // Generate URL slug from chapter title (e.g., "Chapter 11: Accessibility" -> "chapter-11-accessibility")
      // This is needed because chapterSlug may contain API chapter ID (e.g., "IBC2018P6_Ch11")
      const urlSlug = getChapterUrlSlug(chapterItem?.title, chapterItem?.chapterSlug);

      if (urlSlug) {
        // For Chapters, SubChapters, or Sections that have a valid chapter slug
        if (hierarchy.length === 2) {
          // It's a chapter
          return `https://codes.iccsafe.org/content/${documentSlug}/${urlSlug}`;
        } else {
          // It's a section or subsection
          return `https://codes.iccsafe.org/content/${documentSlug}/${urlSlug}#${section.sectionId}`;
        }
      } else {
        // Fallback to book URL if chapter slug cannot be determined
        return `https://codes.iccsafe.org/content/${documentSlug}`;
      }
    }

    // Fallback for book level or any other case
    return `https://codes.iccsafe.org/content/${documentSlug}`;
  }

  /**
   * Generate the appropriate ICC URL for a hierarchy item
   */
  getIccUrlForHierarchyItem(
    section: ComplianceSection,
    hierarchyItem: { title: string; id?: string; chapterSlug?: string },
    itemIndex: number
  ): string {
    const codeBook = section.codeBook;
    if (!codeBook || !hierarchyItem.id) return '#';

    const documentSlug = codeBook.document_slug;
    const hierarchy = section.hierarchy || [];

    // Book level
    if (itemIndex === 0) {
      return `https://codes.iccsafe.org/content/${documentSlug}`;
    }

    // Chapter or section level
    if (hierarchy.length > 1) {
      const chapterItem = hierarchy[1];
      // Generate URL slug from chapter title (e.g., "Chapter 11: Accessibility" -> "chapter-11-accessibility")
      // This is needed because chapterSlug may contain API chapter ID (e.g., "IBC2018P6_Ch11")
      const urlSlug = getChapterUrlSlug(chapterItem?.title, chapterItem?.chapterSlug);

      if (urlSlug) {
        if (itemIndex === 1) {
          // It's a chapter
          return `https://codes.iccsafe.org/content/${documentSlug}/${urlSlug}`;
        } else {
          // It's a section or subsection
          return `https://codes.iccsafe.org/content/${documentSlug}/${urlSlug}#${hierarchyItem.id}`;
        }
      } else {
        // Fallback to book URL if chapter slug cannot be determined
        return `https://codes.iccsafe.org/content/${documentSlug}`;
      }
    }

    // Fallback for book level or any other case
    return `https://codes.iccsafe.org/content/${documentSlug}`;
  }

  // Helper to get the parent section for a given row
  getParentSection(section: ComplianceSection): ComplianceSection | null {
    if (!section.hierarchy || section.hierarchy.length < 2 || !this.complianceData) return null;
    const parentId = section.hierarchy[section.hierarchy.length - 2].id;
    return this.complianceData.sections.find(s => s.sectionId === parentId) || null;
  }

  /** Stable file identity for deployed journey assertions; legacy single-file pages use file 1. */
  getActiveFileId(): string {
    const pageFolderPath = this.activePlanPage?.getPageFolderPath() ?? '';
    return pageFolderPath.match(/\/files\/([^/]+)\/pages\/[^/]+\/?$/)?.[1] ?? '1';
  }

  /** Stable parent identity for semantic tree assertions. */
  getParentSectionId(section: ComplianceSection): string {
    return this.getParentSection(section)?.sectionId ?? '';
  }

  // Returns true if all expandable rows are expanded
  get allExpanded(): boolean {
    if (!this.complianceData) return false;
    const expandable = this.complianceData.sections.filter(s => this.hasChildren(s));
    return expandable.length > 0 && expandable.every(s => this.isExpanded(s));
  }

  clearRelevanceFilter() {
    this.relevanceFilterControl.setValue([]);
    this.menuTrigger.closeMenu();
  }

  clearComplianceFilter() {
    this.complianceFilterControl.setValue([]);
  }

  closeMenu() {
    this.menuTrigger.closeMenu();
  }

  openRationaleDialog(section: any): void {
    const dialogData: RationaleDialogData = {
      pageNumber: this.activePlanPage?.getPageNumber() || 0,
      pageTitle: this.activePlanPage?.getTitle() || '',
      sectionTitle: section.title,
      sectionId: section.sectionId,
      hierarchy: section.hierarchy ? section.hierarchy.map((h: any) => h.title).join(' > ') : '',
      hierarchyArr: section.hierarchy || [],
      relevance: section.relevance,
      rationale: section.rationale,
      codeBook: section.codeBook,
      applicabilityModel: section.applicabilityModel,
      applicabilityCreatedTime: section.applicabilityCreatedTime,
    };

    this.dialog.open(RationaleDialogComponent, {
      data: dialogData,
      width: '600px',
    });
  }

  // Cache for relevance counts to avoid recalculating on each render
  private relevanceCountsCache = new Map<string, { high: number; medium: number; low: number }>();

  // Count relevance values in all descendants for container nodes
  countDescendantRelevance(section: ComplianceSection): { high: number; medium: number; low: number } {
    // Check cache first
    if (this.relevanceCountsCache.has(section.sectionId)) {
      return this.relevanceCountsCache.get(section.sectionId)!;
    }

    const recursiveCount = (sec: ComplianceSection): { high: number; medium: number; low: number } => {
      const counts = { high: 0, medium: 0, low: 0 };
      const children = this.childrenMap.get(sec.sectionId) || [];

      for (const child of children) {
        if (child.relevance === 'High') counts.high++;
        else if (child.relevance === 'Medium') counts.medium++;
        else if (child.relevance === 'Low') counts.low++;

        // Add counts from grandchildren and so on
        const descendantCounts = recursiveCount(child);
        counts.high += descendantCounts.high;
        counts.medium += descendantCounts.medium;
        counts.low += descendantCounts.low;
      }
      return counts;
    };

    const result = recursiveCount(section);

    // Cache the result
    this.relevanceCountsCache.set(section.sectionId, result);
    return result;
  }

  // Check if section is a container with '-' relevance (should show count chips)
  shouldShowRelevanceCountChips(section: ComplianceSection): boolean {
    return section.relevance === '-' && this.hasChildren(section);
  }

  openComplianceReport(section: ComplianceSection): void {
    const newTab: Tab = {
      label: section.title,
      component: ComplianceReportComponent,
      tabId: section.sectionId,
      data: section,
    };
    this.tabService.addTab(newTab);
  }

  private saveCurrentState(): void {
    if (!this.activePlanPage) return;

    const scrollPosition = this.scrollContainer?.nativeElement?.scrollTop || 0;
    const state: ComplianceComponentState = {
      expandedSectionIds: this.expandedSectionIds,
      selectedRelevanceValues: this.selectedRelevanceValues,
      selectedComplianceValues: this.selectedComplianceValues,
      scrollPosition: scrollPosition,
    };

    this.complianceStateService.saveState(
      this.activePlanPage.getArchitecturalPlanId(),
      this.activePlanPage.getPageNumber(),
      state
    );
  }

  private restoreState(): void {
    if (!this.activePlanPage) return;

    const savedState = this.complianceStateService.restoreState(
      this.activePlanPage.getArchitecturalPlanId(),
      this.activePlanPage.getPageNumber()
    );

    if (savedState) {
      ClientLoggingService.log('INFO', '[ComplianceComponent] Restoring saved state:', savedState);

      // Restore expanded sections
      this.expandedSectionIds = savedState.expandedSectionIds;

      // Restore filters
      this.selectedRelevanceValues = savedState.selectedRelevanceValues;
      this.selectedComplianceValues = savedState.selectedComplianceValues;

      // Update form controls
      this.relevanceFilterControl.setValue(this.selectedRelevanceValues, { emitEvent: false });
      this.complianceFilterControl.setValue(this.selectedComplianceValues, { emitEvent: false });

      // Restore scroll position after a brief delay to ensure DOM is ready
      setTimeout(() => {
        if (this.scrollContainer?.nativeElement && savedState.scrollPosition > 0) {
          this.scrollContainer.nativeElement.scrollTop = savedState.scrollPosition;
          // Update scroll shadow state after restoring scroll position
          this.isScrolled = savedState.scrollPosition > 0;
        }
      }, 100);
    }
  }

  onScrollPositionChange(): void {
    // Update scroll state for header shadow
    const scrollTop = this.scrollContainer?.nativeElement?.scrollTop || 0;
    this.isScrolled = scrollTop > 0;

    // Debounce scroll position saving to avoid excessive saves
    if (this.scrollSaveTimeout) {
      clearTimeout(this.scrollSaveTimeout);
    }
    this.scrollSaveTimeout = setTimeout(() => {
      this.saveCurrentState();
    }, 250);
  }

  private scrollSaveTimeout: any;

  // NEW: Section selection management methods

  /**
   * Handle section checkbox change event
   */
  onSectionCheckboxChange(section: ComplianceSection): void {
    ClientLoggingService.log(
      'INFO',
      '[ComplianceComponent] Section selection changed:',
      section.sectionId,
      section.selected
    );
    this.updateSelectedSections();
  }

  /**
   * Update the selectedSections array based on current selection state
   */
  private updateSelectedSections(): void {
    this.selectedSections = this.visibleSections.filter(section => section.selected);
    ClientLoggingService.log('INFO', '[ComplianceComponent] Selected sections updated:', this.selectedSections.length);
  }

  /**
   * Clear all section selections
   */
  clearAllSectionSelections(): void {
    if (this.complianceData) {
      this.complianceData.sections.forEach(section => (section.selected = false));
    }
    this.selectedSections = [];
    ClientLoggingService.log('INFO', '[ComplianceComponent] All section selections cleared');
  }

  /**
   * Update selected sections to match a provided list (for individual chip removal)
   */
  private updateSelectedSectionsFromList(remainingSections: ComplianceSection[]): void {
    ClientLoggingService.log(
      'INFO',
      '[ComplianceComponent] Updating selection to match remaining sections:',
      remainingSections.length
    );

    // Update selectedSections array
    this.selectedSections = [...remainingSections];

    // Update all section objects to reflect new selection state
    if (this.complianceData && this.complianceData.sections) {
      this.complianceData.sections.forEach(section => {
        section.selected = remainingSections.some(selected => selected.sectionId === section.sectionId);
      });
    }

    ClientLoggingService.log(
      'INFO',
      '[ComplianceComponent] Selection updated to',
      this.selectedSections.length,
      'sections'
    );
  }

  /**
   * Get display text for selected sections count
   */
  getSelectedSectionsText(): string {
    const count = this.selectedSections.length;
    return count === 1 ? '1 section selected' : `${count} sections selected`;
  }

  /**
   * Get comma-separated list of selected section names for display
   */
  getSelectedSectionNames(): string {
    return this.selectedSections
      .map(section => (section.title.length > 30 ? section.title.substring(0, 30) + '...' : section.title))
      .join(', ');
  }

  /**
   * Get the appropriate Material icon based on compliance status
   */
  getComplianceIcon(status: string): string {
    switch (status) {
      case 'Compliant':
        return 'verified_user'; // Green checkmark with user icon
      case 'Partially Compliant':
        return 'help_outline'; // Question mark in circle (shield_question not available)
      case 'Non-Compliant':
        return 'gpp_maybe'; // Shield with warning
      case 'Not Applicable':
        return 'block'; // Block/disabled icon
      case 'Unknown':
      case '-':
      default:
        return 'shield'; // Default shield icon for unknown/empty status
    }
  }

  /**
   * Handle actions from the selected sections toolbar
   */
  onSelectedSectionsAction(action: SelectedSectionsToolbarAction): void {
    ClientLoggingService.log('INFO', '[ComplianceComponent] Selected sections action:', action.type);

    switch (action.type) {
      case 'generate-reports':
        this.handleGenerateComplianceReports(action.sections || [], action.forceRecompute);
        break;

      case 'cancel':
        this.clearAllSectionSelections();
        break;

      case 'clear-selection':
        if (action.sections) {
          // Partial clear - update selection to match remaining sections
          this.updateSelectedSectionsFromList(action.sections);
        } else {
          // Full clear
          this.clearAllSectionSelections();
        }
        break;

      default:
        ClientLoggingService.log('WARN', '[ComplianceComponent] Unknown action type:', action.type);
    }
  }

  /**
   * Handle compliance report generation for selected sections
   */
  private async handleGenerateComplianceReports(
    sections: ComplianceSection[],
    forceRecompute?: boolean
  ): Promise<void> {
    if (!this.activePlanPage || sections.length === 0) {
      ClientLoggingService.log('WARN', '[ComplianceComponent] Cannot generate reports: missing page or sections');
      return;
    }

    try {
      // Proactive balance check before making server calls (Issue #553)
      if (
        await this.billingService.guardInsufficientBalance(
          this.snackBar,
          this.router,
          'compliance-report',
          sections.length
        )
      ) {
        return;
      }

      ClientLoggingService.log(
        'INFO',
        `[ComplianceComponent] Starting compliance report generation for ${sections.length} sections`
      );

      // Extract section IDs for the service call
      const sectionIds = sections.map(section => section.sectionId);

      // TODO: Get the current book ID from the compliance data
      // For now, we'll use a default book ID (IBC 2021)
      const currentBookId = sections[0]?.codeBook?.id || '2217'; // Default to IBC 2021

      // NEW (Issue #167): Extract file ID from page folder path
      const pageFolderPath = this.activePlanPage.getPageFolderPath() || '';
      const modernPathMatch = pageFolderPath.match(/\/files\/(\d+)\/pages\/\d+\/?$/);
      const fileId = modernPathMatch ? modernPathMatch[1] : undefined;

      ClientLoggingService.log('INFO', `[ComplianceComponent] Extracted fileId=${fileId} from path: ${pageFolderPath}`);

      // Call the PlanReviewerService to generate compliance reports
      const result = await this.planReviewerService.generatePageSectionComplianceReports(
        this.activePlanPage.getArchitecturalPlanId(),
        this.activePlanPage.getPageNumber(),
        sectionIds,
        currentBookId,
        fileId,
        forceRecompute
      );

      if (!result.success || result.taskIds.length === 0) {
        ClientLoggingService.log('ERROR', '[ComplianceComponent] Failed to start compliance reports:', result.message);
        showBillingAwareErrorSnackbar(this.snackBar, this.router, result.message);
        return;
      }

      // Add tasks to counter service for tracking
      result.taskIds.forEach((taskId, index) => {
        const section = sections[index];
        const sectionTitle = section.title.length > 40 ? section.title.substring(0, 40) + '...' : section.title;
        const taskDisplayName = `Compliance Report - Page ${this.activePlanPage!.getPageNumber()}: ${sectionTitle}`;
        this.taskCounterService.addTask(taskId, this.activePlanPage!.getArchitecturalPlanId(), taskDisplayName);
      });

      const singleTaskId = result.taskIds.length === 1 ? result.taskIds[0] : undefined;
      const actionLabel = singleTaskId ? 'View Task' : 'View Tasks';
      const snackBarRef = this.snackBar.open(
        `Started ${result.taskIds.length} compliance report task(s)`,
        actionLabel,
        {
          duration: 8000,
          horizontalPosition: 'right',
          verticalPosition: 'top',
          panelClass: ['task-started-snackbar'],
        }
      );

      snackBarRef.onAction().subscribe(() => {
        const planId = this.activePlanPage!.getArchitecturalPlanId();
        this.router.navigate(getTaskUrl(planId, singleTaskId));
      });

      ClientLoggingService.log(
        'INFO',
        `[ComplianceComponent] Successfully started ${result.taskIds.length} compliance report tasks`
      );

      // Clear selections after starting generation
      this.clearAllSectionSelections();
    } catch (error) {
      ClientLoggingService.log('ERROR', '[ComplianceComponent] Error generating compliance reports:', error);
      this.snackBar.open('Error generating compliance reports', 'Dismiss', {
        duration: 5000,
        panelClass: ['error-snackbar'],
      });
    }
  }
}
